import { useState, useEffect } from "react";
import { Edit3, Search, Download } from "lucide-react";
import { api, fmt } from "../../lib/api";
import { Avatar, PanelLoading } from "../../components/SmartImage";
import { MemberServiceNotes } from "./blessings";
import { toast } from "sonner";

/* ----------------- MEMBERS ----------------- */
export function Members() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { member, step: 1|2|3 }
  const [q, setQ] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [sortBy, setSortBy] = useState("created_desc");
  const load = () => api.get("/admin/members").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const saveEdit = async () => {
    if (!editing) return;
    const payload = {};
    if (editing.name !== undefined) payload.name = editing.name;
    if (editing.email !== undefined) payload.email = editing.email;
    if (editing.tier !== undefined) payload.tier = editing.tier;
    if (editing.is_active !== undefined) payload.is_active = editing.is_active;
    payload.confirm = true;
    try {
      await api.patch(`/admin/members/${editing.id}`, payload);
      await api.patch(`/admin/members/${editing.id}/note`, { note: editing.admin_note || "" });
      toast.success("Member updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/admin/members/${deleteConfirm.member.id}?confirm=true`);
      toast.success("Member deleted");
      setDeleteConfirm(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  if (!data) return <PanelLoading label="Loading members" />;
  const s = q.trim().toLowerCase();
  const filtered = data.members
    .filter((m) => tierFilter === "all" ? true : (tierFilter === "active" ? m.is_active : m.tier === tierFilter))
    .filter((m) => !s || (m.email || "").toLowerCase().includes(s) || (m.name || "").toLowerCase().includes(s))
    .sort((a, b) => {
      if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "created_asc") return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
  const exportCSV = () => {
    const head = ["Email", "Name", "Tier", "Active", "Joined", "Last login", "Affiliate code", "Earnings ($)"];
    const rows = [head, ...filtered.map((m) => [m.email, m.name, m.tier, m.is_active ? "yes" : "no", m.created_at, m.last_login, m.affiliate_code || "", ((m.affiliate_earnings_cents || 0) / 100).toFixed(2)])];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = `members_${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} members`);
  };
  return (
    <div>
      <div className="overline mb-3">// {data.count_active} active &middot; {data.members.length} total &middot; {filtered.length} shown</div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-textDim absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="member-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="!pl-9" />
        </div>
        <select data-testid="member-tier-filter" value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} className="!w-auto text-xs">
          <option value="all">All tiers</option>
          <option value="active">Active only</option>
          <option value="full">Sovereign</option>
          <option value="foundational">Foundational</option>
          <option value="canceled">Canceled</option>
          <option value="none">None</option>
        </select>
        <select data-testid="member-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="!w-auto text-xs">
          <option value="created_desc">Newest first</option>
          <option value="created_asc">Oldest first</option>
          <option value="name">Name A–Z</option>
        </select>
        <button data-testid="member-export" onClick={exportCSV} className="btn-ghost text-xs"><Download className="w-3 h-3" /> Export CSV</button>
      </div>

      {/* Edit panel */}
      {editing && (
        <div className="panel p-6 mb-6 border-gold">
          <div className="overline mb-3">// EDITING: {editing.email}</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="overline">// NAME</label>
              <input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </div>
            <div>
              <label className="overline">// EMAIL</label>
              <input value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
            </div>
            <div>
              <label className="overline">// TIER</label>
              <select value={editing.tier || "none"} onChange={(e) => setEditing({ ...editing, tier: e.target.value })}>
                <option value="none">None</option>
                <option value="full">Full (Sovereign)</option>
                <option value="foundational">Foundational</option>
                <option value="canceled">Canceled</option>
              </select>
            </div>
            <div>
              <label className="overline">// ACTIVE</label>
              <select value={editing.is_active ? "true" : "false"} onChange={(e) => setEditing({ ...editing, is_active: e.target.value === "true" })}>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="overline">// PRIVATE NOTE — the member can NEVER see this</label>
            <textarea data-testid="member-note" rows={3} value={editing.admin_note || ""} onChange={(e) => setEditing({ ...editing, admin_note: e.target.value })} placeholder="Context, history, follow-ups, special arrangements — visible only to you."/>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs font-mono text-textDim mt-4 pt-4 border-t border-borderGold">
            <span>Joined {fmt.date(editing.created_at)}</span>
            <span>Last login {fmt.date(editing.last_login)}</span>
            <span>Affiliate code {editing.affiliate_code || "—"}</span>
            <span>Earnings {fmt.money(editing.affiliate_earnings_cents || 0)}</span>
            {editing.stripe_customer_id && <span>Stripe {editing.stripe_customer_id}</span>}
            {editing.stripe_plan && <span>Plan {editing.stripe_plan}</span>}
            {editing.admin_note_at && <span>Note saved {fmt.date(editing.admin_note_at)}</span>}
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={saveEdit} className="btn-gold">Save changes</button>
            <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
          </div>
          <MemberServiceNotes memberId={editing.id} />
        </div>
      )}

      {/* Delete confirmation panel */}
      {deleteConfirm && (
        <div className="panel p-6 mb-6 border-ruby">
          <div className="overline text-ruby mb-3">// DELETE MEMBER</div>
          {deleteConfirm.step === 1 && (
            <>
              <p className="text-cream mb-4">Are you sure you want to delete <strong>{deleteConfirm.member.email}</strong>? This cannot be undone.</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm({ ...deleteConfirm, step: 2 })} className="btn-gold !bg-ruby !border-ruby">Yes, delete this member</button>
                <button onClick={() => setDeleteConfirm(null)} className="btn-ghost">Cancel</button>
              </div>
            </>
          )}
          {deleteConfirm.step === 2 && (
            <>
              <p className="text-cream mb-4">This will permanently delete <strong>{deleteConfirm.member.email}</strong> and all their data (notes, bookmarks, posts, comments, transactions). Are you absolutely sure?</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm({ ...deleteConfirm, step: 3 })} className="btn-gold !bg-ruby !border-ruby">Yes, permanently delete</button>
                <button onClick={() => setDeleteConfirm(null)} className="btn-ghost">Cancel</button>
              </div>
            </>
          )}
          {deleteConfirm.step === 3 && (
            <>
              <p className="text-cream mb-4">Final confirmation. Type <strong className="text-ruby">DELETE</strong> below to confirm:</p>
              <input
                placeholder='Type "DELETE" to confirm'
                className="mb-4"
                onChange={(e) => {
                  if (e.target.value === "DELETE") {
                    confirmDelete();
                  }
                }}
              />
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="btn-ghost">Cancel</button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="overflow-x-auto panel">
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b border-borderGold"><th className="p-3 overline">Email</th><th className="p-3 overline">Name</th><th className="p-3 overline">Tier</th><th className="p-3 overline">Joined</th><th className="p-3 overline">Last login</th><th className="p-3 overline">Actions</th></tr></thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.id} className="border-b border-borderGold/50">
                <td className="p-3 text-cream">{m.email}</td>
                <td className="p-3 text-textMuted">{m.name}{m.admin_note ? <span className="ml-2 overline text-gold" title={m.admin_note}>• NOTE</span> : null}</td>
                <td className="p-3"><span className="overline">{m.tier}</span></td>
                <td className="p-3 text-xs font-mono text-textDim">{fmt.date(m.created_at)}</td>
                <td className="p-3 text-xs font-mono text-textDim">{fmt.date(m.last_login)}</td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <button data-testid={`member-edit-${m.id}`} onClick={() => setEditing({ ...m })} className="btn-ghost text-xs !py-1 !px-2"><Edit3 className="w-3 h-3"/></button>
                    <button onClick={() => setDeleteConfirm({ member: m, step: 1 })} className="btn-ghost text-xs !py-1 !px-2 !border-ruby !text-ruby">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ----------------- LEADS ----------------- */
export function Leads() {
  const [data, setData] = useState(null);
  const [wl, setWl] = useState(null);
  const load = () => api.get("/admin/leads").then((r) => setData(r.data));
  useEffect(() => {
    load();
    api.get("/admin/waitlist").then((r) => setWl(r.data));
  }, []);
  const update = async (id, patch) => {
    try { await api.patch(`/admin/leads/${id}`, patch); load(); }
    catch { toast.error("Update failed"); }
  };
  if (!data || !wl) return <PanelLoading label="Loading leads" />;
  const STATUSES = ["new", "contacted", "nurturing", "converted", "cold"];
  return (
    <div>
      <div className="grid md:grid-cols-2 gap-px bg-borderGold mb-8">
        <div className="bg-void p-6">
          <div className="overline mb-2">// LEADS &middot; BLOG OPTINS</div>
          <div className="font-display text-5xl text-cream">{data.count}</div>
        </div>
        <div className="bg-void p-6">
          <div className="overline mb-2">// WAITLIST</div>
          <div className="font-display text-5xl text-cream">{wl.count}</div>
        </div>
      </div>
      <h3 className="font-display text-xl text-cream mb-3">Recent leads</h3>
      <p className="text-textMuted text-sm mb-4">Add a pipeline status and a private note to any lead — only you can see these. Use it to track who you've reached out to and who's warming up.</p>
      <div className="space-y-2">
        {data.leads.map((l) => (
          <div key={l.id} className="panel p-4" data-testid={`lead-${l.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-cream">{l.email}</div>
                <div className="overline">{l.source} · {fmt.datetime(l.created_at)}</div>
              </div>
              <select data-testid={`lead-status-${l.id}`} value={l.status || "new"} onChange={(e) => update(l.id, { status: e.target.value })} className="!w-auto text-xs">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <input
              data-testid={`lead-note-${l.id}`}
              defaultValue={l.admin_note || ""}
              placeholder="Private note (only you see this) — press Enter to save"
              className="mt-3 text-sm"
              onKeyDown={(e) => { if (e.key === "Enter") { update(l.id, { note: e.target.value }); toast.success("Note saved"); } }}
              onBlur={(e) => { if (e.target.value !== (l.admin_note || "")) update(l.id, { note: e.target.value }); }}
            />
          </div>
        ))}
        {data.leads.length === 0 && <p className="text-textMuted">No leads yet.</p>}
      </div>
    </div>
  );
}

/* ----------------- STEWARD OF STEWARDS ----------------- */
export function StewardOfStewards() {
  const [data, setData] = useState({ leaders: [], total: 0, total_payout_cents: 0 });
  useEffect(() => { api.get("/admin/members").then((r) => {
    const list = (r.data.members || []).filter((m) => (m.affiliate_earnings_cents || 0) > 0)
      .sort((a, b) => (b.affiliate_earnings_cents||0) - (a.affiliate_earnings_cents||0));
    setData({ leaders: list, total: list.length, total_payout_cents: list.reduce((s, m) => s + (m.affiliate_earnings_cents||0), 0) });
  }); }, []);
  return (
    <div>
      <div className="overline mb-3">// STEWARD OF STEWARDS · SUPPORT THE STEWARDS WHO SOW</div>
      <h2 className="font-display text-3xl text-cream">Lead the multipliers.</h2>
      <p className="text-textMuted mt-2 max-w-3xl">These are the members already bringing others through. Reach out personally — a one-line DM to your top stewards each week compounds faster than any ad spend.</p>
      <div className="grid sm:grid-cols-3 gap-px bg-borderGold mt-6">
        <div className="bg-void p-5"><div className="overline">// ACTIVE STEWARDS</div><div className="font-display text-4xl text-cream">{data.total}</div></div>
        <div className="bg-void p-5"><div className="overline">// TOTAL PAID OUT</div><div className="font-display text-4xl text-cream">{fmt.money(data.total_payout_cents)}</div></div>
        <div className="bg-void p-5"><div className="overline">// THIS WEEK'S ACTION</div><div className="text-cream mt-2">Send the top 3 stewards a personal voice note. Ask: "What's the next piece you wish you had to share?" Then build it.</div></div>
      </div>
      <h3 className="font-display text-2xl text-cream mt-10 mb-4">Leaderboard</h3>
      <div className="space-y-2">
        {data.leaders.map((m, i) => (
          <div key={m.id} className="panel p-4 flex items-center gap-4">
            <div className="font-display text-3xl text-gold w-10">#{i+1}</div>
            <div className="w-10 h-10 border border-borderGold bg-surface overflow-hidden">
              <Avatar src={m.avatar_url} name={m.name} />
            </div>
            <div className="flex-1">
              <div className="text-cream font-medium">{m.name}</div>
              <div className="text-xs font-mono text-textDim">{m.email}</div>
            </div>
            <div className="text-right">
              <div className="font-display text-2xl text-gold">{fmt.money(m.affiliate_earnings_cents||0)}</div>
              <a href={`/dm/${m.id}`} className="overline hover:text-gold">Send DM →</a>
            </div>
          </div>
        ))}
        {data.leaders.length === 0 && <p className="text-textMuted">No stewardships have generated payouts yet.</p>}
      </div>
      <div className="panel p-6 mt-10">
        <div className="overline mb-3">// WEEKLY PLAYBOOK</div>
        <ol className="space-y-2 text-cream/90">
          <li>1. DM your top 3 stewards. Thank them by name.</li>
          <li>2. Send them ONE ad-copy variant they can post today (from the Ad Copy tab).</li>
          <li>3. Tell them the next drop / article coming so they can pre-warm their list.</li>
          <li>4. Feature one steward's testimony as social proof this week.</li>
          <li>5. If a steward is near a milestone ($100, $500, $1k), call them.</li>
        </ol>
      </div>
    </div>
  );
}
